import { ApiError } from '~/utils/errors';

// Pure API service for billing operations - no state management
export const billingService = {
    async getInvoices(personId: number, page: number = 1, perPage: number = 10): Promise<any> {
        const nuxtApp = useNuxtApp();
        
        try {
            const { data, status, message } = await usePbApi<any>(
                nuxtApp.$api.routes.people.billing.invoices.get(personId, page, perPage)
            );

            if (status !== 200 || !data) {
                throw new ApiError(message || 'Failed to fetch invoices', status);
            }

            return data;
        } catch (error: any) {
            throw ApiError.fromFetchError(error);
        }
    },

    async downloadInvoice(personId: number, invoiceId: string): Promise<Blob> {
        const nuxtApp = useNuxtApp();
        
        try {
            const { data, status, message } = await usePbApi<Blob>(
                nuxtApp.$api.routes.people.billing.invoices.download(personId, invoiceId),
                {
                    responseType: 'blob'
                }
            );

            if (status !== 200 || !data) {
                throw new ApiError(message || 'Failed to download invoice', status);
            }

            return data;
        } catch (error: any) {
            throw ApiError.fromFetchError(error);
        }
    },

    async getCreditBalance(personId: number): Promise<number> {
        const nuxtApp = useNuxtApp();

        try {
            const { data, status, message } = await usePbApi<any>(
                nuxtApp.$api.routes.people.billing.creditBalance(personId)
            );
            if (status !== 200 || !data) {
                throw new ApiError(message || 'Failed to fetch credit balance', status);
            }
            return data.data.credit_balance || 0;
        } catch (error: any) {
            throw ApiError.fromFetchError(error);
        }
    },

    async purchaseGiftCertificate(personId: number, name: string, email: string, amount: number, paymentMethodId?: string): Promise<any> {
        const nuxtApp = useNuxtApp();
        
        try {
            const payload: any = {
                name,
                email,
                amount,
            };

            // Add payment method if provided
            if (paymentMethodId) {
                payload.payment_method = paymentMethodId;
            }

            const { data, status, message } = await usePbApi<any>(
                nuxtApp.$api.routes.people.billing.giftCertificates.purchase(personId),
                {
                    method: 'POST',
                    body: payload,
                }
            );

            if (status !== 201) {
                throw new ApiError(message || 'Failed to purchase gift certificate', status);
            }

            return data;
        } catch (error: any) {
            throw ApiError.fromFetchError(error);
        }
    },

    async applyGiftCertificate(personId: number, code: string): Promise<any> {
        const nuxtApp = useNuxtApp();
        
        try {
            const { data, status, message } = await usePbApi<any>(
                nuxtApp.$api.routes.people.billing.giftCertificates.apply(personId),
                {
                    method: 'POST',
                    body: {
                        code,
                    },
                }
            );

            if (status !== 201 || !data) {
                throw new ApiError(message || 'Failed to apply gift certificate', status);
            }

            return data;
        } catch (error: any) {
            throw ApiError.fromFetchError(error);
        }
    },

    async sendGiftCertificateEmail(personId: number, recipientName: string, recipientEmail: string, code: string): Promise<any> {
        const nuxtApp = useNuxtApp();
        
        try {
            const { data, status, message } = await usePbApi<any>(
                nuxtApp.$api.routes.people.billing.giftCertificates.email(personId, code),
                {
                    method: 'POST',
                    body: {
                        recipientName,
                        recipientEmail,
                    },
                }
            );

            if (status !== 200 || !data) {
                throw new ApiError(message || 'Failed to send gift certificate email', status);
            }

            return data;
        } catch (error: any) {
            throw ApiError.fromFetchError(error);
        }
    },
}; 